import wadiRum from '../../public/img/landing/wadi-rum.jpg';
import hillCountry from '../../public/img/landing/hill-country.jpg';
import Image from '../FutureImage';
import { fraunces } from './fonts';
import FeaturedPostsStrip from './FeaturedPostsStrip';
import {
  CheckItem,
  DisplayHeading,
  Eyebrow,
  INK,
  MUTED,
  PAPER_CARD,
  PlaneIcon,
  Reveal,
} from './ui';

function ToolbarChip({ children, active }) {
  return (
    <span
      className={`tw-flex tw-h-7 tw-min-w-[28px] tw-items-center tw-justify-center tw-rounded-md tw-px-1.5 tw-text-xs tw-font-semibold ${
        active
          ? 'tw-bg-primary-600 tw-text-white'
          : 'tw-bg-[#F1ECDF] dark:tw-bg-[#26382F] tw-text-[#5F6F69] dark:tw-text-[#93A69E]'
      }`}
    >
      {children}
    </span>
  );
}

function WindowDots() {
  return (
    <div className="tw-flex tw-gap-1.5" aria-hidden="true">
      <span className="tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-[#E8B4B0]" />
      <span className="tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-[#EBD8A8]" />
      <span className="tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-[#B8D4BC]" />
    </div>
  );
}

function EditorMockup() {
  return (
    <div
      className={`${PAPER_CARD} tw-overflow-hidden tw-rounded-2xl tw-shadow-[0_24px_50px_-24px_rgba(29,45,40,0.35)]`}
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-border-0 tw-border-b tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-px-4 tw-py-3">
        <WindowDots />
        <span className="tw-font-mono tw-text-[10px] tw-tracking-[0.2em] tw-text-[#9AA59E]">
          DRAFT · SAVED JUST NOW
        </span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-border-0 tw-border-b tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-px-4 tw-py-2.5">
        <ToolbarChip>B</ToolbarChip>
        <ToolbarChip>
          <em>I</em>
        </ToolbarChip>
        <ToolbarChip>H2</ToolbarChip>
        <ToolbarChip>🖼</ToolbarChip>
        <ToolbarChip>📍</ToolbarChip>
        <span className="tw-flex-1" />
        <ToolbarChip active>✨ AI Assist</ToolbarChip>
      </div>
      <div className="tw-p-5 sm:tw-p-6">
        <p
          className={`${fraunces.className} ${INK} tw-m-0 tw-text-xl tw-font-semibold tw-leading-snug sm:tw-text-2xl`}
        >
          Chasing trains through Sri Lanka’s hill country
        </p>
        <p className="tw-mb-0 tw-mt-2 tw-font-mono tw-text-[10px] tw-tracking-[0.2em] tw-text-primary-700 dark:tw-text-primary-300">
          📍 ELLA, SRI LANKA
        </p>
        <p className={`${MUTED} tw-mb-0 tw-mt-3 tw-text-sm tw-leading-relaxed`}>
          The 9:30 from Kandy left an hour late, which by hill-country
          standards meant it was on time. We hung out of the blue carriage
          doors as the tea fields rolled past…
        </p>
        <div className="tw-relative tw-mt-4 tw-overflow-hidden tw-rounded-lg">
          <Image
            src={hillCountry}
            alt="Misty green hills of Sri Lanka’s hill country at golden hour"
            sizes="(max-width: 992px) 90vw, 40vw"
            className="tw-h-36 tw-w-full tw-object-cover sm:tw-h-44"
          />
          <span className="tw-absolute tw-bottom-2.5 tw-left-2.5 tw-rounded-full tw-bg-black/50 tw-px-2.5 tw-py-1 tw-font-mono tw-text-[10px] tw-tracking-widest tw-text-white">
            GALLERY · 14 PHOTOS
          </span>
        </div>
        <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-xl tw-bg-primary-600/[0.07] dark:tw-bg-primary-300/10 tw-p-3">
          <span className="tw-text-base" aria-hidden="true">
            ✨
          </span>
          <span className="tw-rounded-full tw-bg-white dark:tw-bg-[#15231E] tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 tw-shadow-sm">
            Tighten this paragraph
          </span>
          <span className="tw-rounded-full tw-bg-white dark:tw-bg-[#15231E] tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 tw-shadow-sm">
            Fix my grammar
          </span>
        </div>
      </div>
    </div>
  );
}

function BrowserMockup() {
  return (
    <div className="tw-relative">
      <div
        className={`${PAPER_CARD} tw-overflow-hidden tw-rounded-2xl tw-shadow-[0_24px_50px_-24px_rgba(29,45,40,0.35)]`}
      >
        <div className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-px-4 tw-py-3">
          <WindowDots />
          <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-full tw-bg-[#F1ECDF] dark:tw-bg-[#26382F] tw-px-4 tw-py-1.5">
            <svg
              className="tw-h-3 tw-w-3 tw-text-primary-700 dark:tw-text-primary-300"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="tw-font-mono tw-text-xs tw-text-[#5F6F69] dark:tw-text-[#93A69E]">
              www.saltandsummits.com
            </span>
          </div>
        </div>
        <div className="tw-bg-[#FFFDF8] dark:tw-bg-[#10100E] tw-px-5 tw-pb-5 tw-pt-4">
          <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between">
            <span
              className={`${fraunces.className} ${INK} tw-text-sm tw-font-bold tw-tracking-wide`}
            >
              SALT &amp; SUMMITS
            </span>
            <span className="tw-flex tw-gap-4 tw-font-mono tw-text-[10px] tw-tracking-[0.18em] tw-text-[#9AA59E]">
              <span>STORIES</span>
              <span className="tw-hidden sm:tw-inline">MAP</span>
              <span>ABOUT</span>
            </span>
          </div>
          <div className="tw-relative tw-overflow-hidden tw-rounded-xl">
            <Image
              src={wadiRum}
              alt="Sunrise over the sandstone mountains of Wadi Rum, Jordan"
              sizes="(max-width: 992px) 90vw, 40vw"
              className="tw-h-44 tw-w-full tw-object-cover sm:tw-h-56"
            />
            <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/60 tw-via-black/10 tw-to-transparent" />
            <div className="tw-absolute tw-bottom-4 tw-left-4 tw-right-4">
              <p className="tw-mb-1 tw-font-mono tw-text-[10px] tw-tracking-[0.22em] tw-text-amber-300">
                JORDAN · 8 MIN READ
              </p>
              <p
                className={`${fraunces.className} tw-m-0 tw-text-xl tw-font-semibold tw-leading-tight tw-text-white sm:tw-text-2xl`}
              >
                Sunrise over Wadi Rum
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="tw-absolute -tw-right-3 -tw-top-4 tw-rotate-2 tw-rounded-full tw-bg-primary-600 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-white tw-shadow-lg sm:-tw-right-6">
        ⚡ Up to 19× faster than WordPress
      </div>
      <div className="tw-absolute -tw-bottom-4 -tw-left-2 -tw-rotate-2 tw-rounded-full tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-white dark:tw-bg-[#15231E] tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-[#1D2D28] dark:tw-text-[#EBE6D9] tw-shadow-lg sm:-tw-left-6">
        🔒 Free SSL · Global CDN
      </div>
    </div>
  );
}

function EarnMockup() {
  const rows = [
    {
      icon: '📰',
      label: 'Display ads',
      caption: 'Add your AdSense code and earn per view',
      pill: 'ADSENSE',
      pillActive: true,
    },
    {
      icon: '🎒',
      label: 'Affiliate links',
      caption: 'Drop in links for gear & stays you love',
      pill: 'BUILT-IN',
      pillActive: true,
    },
    {
      icon: '🪙',
      label: 'Hive rewards',
      caption: 'Upvotes from the community, paid in crypto',
      pill: 'OPTIONAL',
      pillActive: false,
    },
  ];
  return (
    <div
      className={`${PAPER_CARD} tw-rounded-2xl tw-p-5 tw-shadow-[0_24px_50px_-24px_rgba(29,45,40,0.35)]`}
    >
      <p className="tw-mb-4 tw-mt-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-[#5F6F69] dark:tw-text-[#93A69E]">
        Monetization · set up once
      </p>
      <div className="tw-flex tw-flex-col tw-gap-3">
        {rows.map(row => (
          <div
            key={row.label}
            className="tw-flex tw-items-center tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F] tw-bg-[#FFFDF8] dark:tw-bg-[#1A2A24] tw-p-4"
          >
            <span className="tw-text-2xl" aria-hidden="true">
              {row.icon}
            </span>
            <div className="tw-flex-1">
              <p className={`${INK} tw-m-0 tw-text-[15px] tw-font-semibold`}>
                {row.label}
              </p>
              <p className={`${MUTED} tw-m-0 tw-text-xs`}>{row.caption}</p>
            </div>
            <span
              className={`tw-rounded-full tw-px-3 tw-py-1 tw-font-mono tw-text-[10px] tw-font-semibold tw-tracking-widest ${
                row.pillActive
                  ? 'tw-bg-primary-600 tw-text-white'
                  : 'tw-border tw-border-solid tw-border-amber-600/50 tw-text-amber-700 dark:tw-text-amber-400'
              }`}
            >
              {row.pill}
            </span>
          </div>
        ))}
      </div>
      <p className={`${MUTED} tw-mb-0 tw-mt-4 tw-text-xs tw-italic`}>
        Everything is opt-in — you decide what runs on your blog.
      </p>
    </div>
  );
}

const STOPS = [
  {
    number: '01',
    tag: 'WRITE',
    title: 'An editor that loves travel stories as much as you do.',
    body:
      'Built for the way travel is actually written: on night buses, in hostel lobbies, three weeks after the trip. Drop in your photos and notes — the rest feels effortless.',
    bullets: [
      'AI Blogger drafts from your own photos and an interview about the trip. It invents nothing, and you stay the author.',
      'Photo galleries, interactive maps, day-by-day itineraries, Instagram and YouTube embeds. All blocks in the editor, none of them plugins.',
      'A clean block editor that gets out of the way, on any device.',
    ],
    Visual: EditorMockup,
  },
  {
    number: '02',
    tag: 'PUBLISH',
    title: 'Your blog. Your domain. Zero maintenance.',
    body:
      'A standalone blog that looks hand-crafted and loads instantly anywhere in the world. It also sorts your writing by place while you write it.',
    bullets: [
      'Pin a post to a place and TravelFeed builds the country, region and city pages for it, linked to each other and ready for Google.',
      'Your archive sorts itself by place and by date, so there is no menu to keep tidy.',
      'Connect your own domain, or start on a free subdomain — SSL and global CDN included.',
      'Up to 19× faster than WordPress, with zero plugins to update. Ever.',
    ],
    Visual: BrowserMockup,
  },
  {
    number: '03',
    tag: 'GROW',
    title: 'Readers from day one — not after two years of SEO.',
    body:
      'Every post also publishes into the TravelFeed community, where thousands of travelers and our curation team are already reading. Your first audience is built in.',
    bullets: [
      'Publish into a community of thousands of travelers — not into the void.',
      'Standout posts get featured by our curation team and shown to everyone.',
      'TravelFeed’s domain authority helps your stories rank in search.',
      'Every post gets share cards made for it, including the vertical pin Pinterest wants.',
      'Already blogging? Guides help you migrate from WordPress or Blogger.',
    ],
    Visual: null, // FeaturedPostsStrip, injected with real data
  },
  {
    number: '04',
    tag: 'EARN',
    title: 'Make your adventures help pay for the next ones.',
    body:
      'Monetization is built in, not bolted on. Pick the methods that fit your blog, set them up once, and keep full control.',
    bullets: [
      'Run display ads with Google AdSense — paste your code in the dashboard.',
      'Add affiliate links for the gear, stays and insurance you already recommend.',
      'Optional: cross-post to Hive and earn crypto rewards when readers upvote.',
    ],
    Visual: EarnMockup,
  },
];

function StopConnector() {
  return (
    <div
      aria-hidden="true"
      className="tw-mx-auto tw-my-4 tw-flex tw-w-min tw-flex-col tw-items-center tw-gap-1 tw-py-6"
    >
      <span className="tw-h-16 tw-w-px tw-border-0 tw-border-l tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] [border-left-width:2px]" />
      <PlaneIcon className="tw-h-5 tw-w-5 tw-rotate-180 tw-text-primary-600/60 dark:tw-text-primary-300/50" />
    </div>
  );
}

export default function Itinerary({ featuredPosts }) {
  return (
    <section id="itinerary" className="tw-scroll-mt-24 tw-py-20 sm:tw-py-28">
      <div className="tw-mx-auto tw-max-w-7xl tw-px-4 sm:tw-px-6 lg:tw-px-8">
        <Reveal className="tw-mx-auto tw-max-w-3xl tw-text-center">
          <Eyebrow>The itinerary</Eyebrow>
          <DisplayHeading className="tw-text-4xl tw-leading-[1.1] sm:tw-text-5xl">
            Four stops between “someday” and “published.”
          </DisplayHeading>
          <p className={`${MUTED} tw-mx-auto tw-mb-0 tw-mt-5 tw-max-w-xl tw-text-lg`}>
            Everything a travel blog needs, already packed. Here’s the route.
          </p>
        </Reveal>

        <div className="tw-mt-16 sm:tw-mt-20">
          {STOPS.map((stop, i) => {
            const reversed = i % 2 === 1;
            return (
              <div key={stop.number}>
                {i > 0 && <StopConnector />}
                <Reveal>
                  <div className="tw-grid tw-grid-cols-1 tw-items-center tw-gap-10 lg:tw-grid-cols-2 lg:tw-gap-20">
                    <div className={reversed ? 'lg:tw-order-2' : ''}>
                      <div className="tw-mb-5 tw-flex tw-items-center tw-gap-3">
                        <span className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-dashed tw-border-primary-600 dark:tw-border-primary-300 tw-font-mono tw-text-sm tw-font-bold tw-text-primary-700 dark:tw-text-primary-300 [border-width:1.5px]">
                          {stop.number}
                        </span>
                        <span className="tw-font-mono tw-text-xs tw-font-semibold tw-tracking-[0.28em] tw-text-primary-700 dark:tw-text-primary-300">
                          STOP {stop.number} — {stop.tag}
                        </span>
                      </div>
                      <DisplayHeading
                        as="h3"
                        className="tw-text-3xl tw-leading-[1.12] sm:tw-text-4xl"
                      >
                        {stop.title}
                      </DisplayHeading>
                      <p className={`${MUTED} tw-mb-0 tw-mt-4 tw-text-base tw-leading-relaxed sm:tw-text-lg`}>
                        {stop.body}
                      </p>
                      <ul className="tw-m-0 tw-mt-6 tw-flex tw-list-none tw-flex-col tw-gap-3.5 tw-p-0">
                        {stop.bullets.map(bullet => (
                          <CheckItem key={bullet}>{bullet}</CheckItem>
                        ))}
                      </ul>
                    </div>
                    <div className={reversed ? 'lg:tw-order-1' : ''}>
                      {stop.Visual ? (
                        <stop.Visual />
                      ) : (
                        <FeaturedPostsStrip posts={featuredPosts} />
                      )}
                    </div>
                  </div>
                </Reveal>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
